import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DatePicker, Select, Skeleton } from "antd";
import { fetchWards } from "api/location";
import { fetchReports } from "api/reports";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import {
  Bar,
  BarChart,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DownArrow,
  GreenScale,
  Incidents,
  RedScale,
  Resolution,
  Response,
  SelectDropdown,
  UpArrow,
} from "themes/Icon";
import TagComp from "themes/elements/Tag";
import "./war-report.scss";

const Card = ({ title, value, subtitle, children, icon, scale }) => {
  return (
    <div className="flex gap-3 card-container">
      <span>{icon}</span>
      <div className="flex gap-2 flex-column">
        <h4 className="card-title">{title}</h4>
        <div className="flex gap-2 dec-container">
          {value && <h2 className="card-value">{value}</h2>}
          {subtitle && (
            <div className="flex gap-2 align-center">
              <p className="dec">{subtitle}</p>
              <span>{scale === "red" ? <RedScale /> : <GreenScale />}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomBarShape = (props) => {
  const { fill, x, y, width, height } = props;
  const radius = 12;

  return (
    <path
      d={`M${x},${y + radius}
          a${radius},${radius} 0 0 1 ${radius},-${radius}
          h${width - 2 * radius}
          a${radius},${radius} 0 0 1 ${radius},${radius}
          v${height - radius}
          h-${width}
          Z`}
      fill={fill}
    />
  );
};

const formatYAxis = (tickItem) => {
  return `${tickItem / 1000}K`;
};

const WardsReport = () => {
  const [databar, setDatabar] = useState([]);
  const [data, setData] = useState({
    totalIncidents: null,
    avgResponseTime: null,
    resolutionRate: null,
    ticketsResolved: [],
    topIncidents: [],
  });
  const [wards, setWards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedMonths, setSelectedMonths] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [selectedWards, setSelectedWards] = useState([]);

  useEffect(() => {
    const transformedData = data?.perDayIncidents?.map((item) => ({
      name: item?.reported_date,
      Tickets: parseInt(item?.count),
    }));
    setDatabar(transformedData);
  }, [data]);

  const pidata = [
    {
      name: "Resolved",
      value: data?.ticketsResolved[0]?.resolved_tickets
        ? Number(data?.ticketsResolved[0]?.resolved_tickets)
        : null,
      color: "#00905E",
    }, // Adjust colors as needed
    {
      name: "Pending",
      value: data?.ticketsResolved[0]?.pending_tickets
        ? Number(data?.ticketsResolved[0]?.pending_tickets)
        : null,
      color: "#744B00",
    },
  ];

  const handleWardChange = (selectedWardCodes) => {
    const numericWardCodes = selectedWardCodes.map((code) =>
      parseInt(code, 10)
    );
    setSelectedWards(numericWardCodes);
  };

  const loadReports = async () => {
    try {
      setIsLoading(true);
      const params = { selected_month: selectedMonths, limit: "5" };
      if (selectedWards && selectedWards?.length > 0) {
        params.ward_code = JSON.stringify(selectedWards);
      }
      const res = await fetchReports(params);
      setData(res);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const loadWards = async () => {
    try {
      setIsLoading(true);
      const res = await fetchWards();
      setWards(
        res?.map((ward) => ({
          label: ward.ward_name,
          value: Number(ward.ward_code),
        }))
      );
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWards();
  }, []);

  useEffect(() => {
    loadReports();
  }, [selectedMonths, selectedWards]);

  const tagRender = (props) => {
    const { label, onClose } = props;
    return <TagComp onClose={onClose}>{label}</TagComp>;
  };

  const disableFutureMonths = (current) => {
    const today = new Date();
    return current && current.valueOf() > today.setHours(0, 0, 0, 0);
  };

  // Generic Skeleton Loader for cards and charts
  const CardSkeleton = () => (
    <div className="card-container skeleton" style={{ padding: "20px" }}>
      <Skeleton.Input style={{ width: 200, height: 20 }} active={true} />
      <Skeleton.Input
        style={{ width: 100, height: 40 }}
        active={true}
        size="large"
      />
    </div>
  );

  const ChartSkeleton = () => (
    <div style={{ width: "100%", height: 300 }}>
      <Skeleton active />
    </div>
  );

  const noDataStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100px",
    fontSize: "20px",
    textAlign: "center",
    borderRadius: "8px",
    margin: "20px", // Extra style to ensure the card is not touching the edges of the viewport
    padding: "20px", // Padding inside the card
  };

  const handleMonthChange = (date, dateString) => {
    // Here 'dateString' is formatted as 'MM-YYYY' because of the DatePicker format
    // You can convert it back or use 'date' object to get a standard format like 'YYYY-MM'
    const formattedDate = date ? date.format("YYYY-MM") : "";
    setSelectedMonths(formattedDate);
  };

  if (isLoading || !data) {
    // Check for isLoading or if data is null
    return (
      <div className="page-wrapper flex flex-column gap-6">
        <div className="flex justify-between">
          <h2 className="page-title mt-2">Reports</h2>
          <div className="flex gap-2">
            <Select
              mode="multiple"
              suffixIcon={<SelectDropdown />}
              allowClear
              tagRender={tagRender}
              style={{ width: "100%" }}
              className="chip-select"
              popupClassName="select-popover"
              placeholder="Wards"
              onChange={handleWardChange}
              value={selectedWards}
              options={wards}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
              filterOption={(input, option) =>
                option?.label.toLowerCase().includes(input.toLowerCase())
              }
            />
            <DatePicker
              format="MM-YYYY"
              style={{ width: 200 }}
              picker="month"
              placeholder="Select month"
              onChange={handleMonthChange}
              defaultValue={dayjs(selectedMonths, "YYYY-MM")}
              disabledDate={disableFutureMonths}
              inputReadOnly={true}
              allowClear={false}
            />
          </div>
        </div>
        <div className="flex gap-40 items-start w-full mb-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
        <ChartSkeleton />
        <div className="reports">
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      </div>
    );
  }
  function calculatePercentageDifference(prev, curr) {
    if (prev === 0) {
      if (curr === 0) {
        return "0%"; // No change, both are zero
      }
      // Use a baseline of 1 to calculate percentage change
      return `+${curr}%`;
    }
    let difference = curr - prev;
    let percentageDifference = (difference / prev) * 100;
    // Format the output with a sign (+ or -) to indicate the direction of the change
    return `${difference >= 0 ? "+" : ""}${percentageDifference.toFixed(2)}%`;
  }

  return (
    <div className="page-wrapper flex flex-column gap-6 ">
      <div className="flex justify-between">
        <h2 className="page-title mt-2">Reports</h2>
        <div className="flex gap-2">
          <Select
            mode="multiple"
            suffixIcon={<SelectDropdown />}
            allowClear
            tagRender={tagRender}
            style={{ width: "100%" }}
            className="chip-select"
            popupClassName="select-popover"
            placeholder="Wards"
            onChange={handleWardChange}
            value={selectedWards}
            options={wards}
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues?.length}`}
            filterOption={(input, option) =>
              option?.label.toLowerCase().includes(input.toLowerCase())
            }
          />

          <DatePicker
            format="MM-YYYY"
            style={{ width: 200 }}
            picker="month"
            placeholder="Select month"
            onChange={handleMonthChange}
            defaultValue={dayjs(selectedMonths, "YYYY-MM")}
            disabledDate={disableFutureMonths}
            inputReadOnly={true}
            allowClear={false}
          />
        </div>
      </div>

      {data.totalIncidents && (
        <div className="flex gap-40 items-start w-full mb-4">
          <Card
            title="All time incidents"
            value={data?.totalIncidents[0]?.curr_count}
            subtitle={`${calculatePercentageDifference(
              Number(data?.totalIncidents[0]?.prev_count),
              Number(data?.totalIncidents[0]?.curr_count)
            )}  Last month`}
            icon={<Incidents />}
            scale={
              calculatePercentageDifference(
                Number(data?.totalIncidents[0]?.prev_count),
                Number(data?.totalIncidents[0]?.curr_count)
              ).includes("-")
                ? "red"
                : "green"
            }
          />
          <Card
            title="Avg. response time"
            value={`${data?.avgResponseTime[0]?.avg_response_time_curr} hrs`}
            subtitle={`${calculatePercentageDifference(
              Number(data?.avgResponseTime[0]?.avg_response_time_prev),
              Number(data?.avgResponseTime[0]?.avg_response_time_curr)
            )}  Last month`}
            icon={<Response />}
            scale={
              calculatePercentageDifference(
                Number(data?.avgResponseTime[0]?.avg_response_time_prev),
                Number(data?.avgResponseTime[0]?.avg_response_time_curr)
              ).includes("-")
                ? "red"
                : "green"
            }
          />
          <Card
            title="Resolution rate"
            value={data?.resolutionRate[0]?.resolution_rate_curr}
            subtitle={`${calculatePercentageDifference(
              Number(data?.resolutionRate[0]?.resolution_rate_prev),
              Number(data?.resolutionRate[0]?.resolution_rate_curr)
            )}  Last month`}
            icon={<Resolution />}
            scale={
              calculatePercentageDifference(
                Number(data?.resolutionRate[0]?.resolution_rate_prev),
                Number(data?.resolutionRate[0]?.resolution_rate_curr)
              ).includes("-")
                ? "red"
                : "green"
            }
          />
        </div>
      )}
      <div className="bar-chart-container">
        <h2 className="title">Tickets raised by citizens</h2>
        {databar?.length > 0 ? (
          <ResponsiveContainer width="100%" height={263}>
            <BarChart
              data={databar}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis dataKey="name" />
              <YAxis tickFormatter={formatYAxis}>
                <Label
                  value="Tickets raised"
                  angle={-90}
                  position="insideLeft"
                  style={{
                    textAnchor: "middle",
                    color: "#121212",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "130%" /* 18.2px */,
                  }}
                  offset={0}
                />
              </YAxis>{" "}
              <Tooltip />
              <Bar dataKey="Tickets" fill="#FEBF4B" shape={<CustomBarShape />}>
                {databar?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry?.color || "#FEBF4B"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={noDataStyle}>
            <span>
              <FontAwesomeIcon icon={faInfoCircle} /> No data available
            </span>
          </div>
        )}
      </div>
      <div className="reports">
        <div className="incidents">
          <h2 className="incidents-title">Top incidents</h2>
          {data?.topIncidents?.length > 0 ? (
            <>
              {data?.topIncidents?.map((incident, index) => (
                <div key={index} className="incident-row">
                  <div className="incident-name">{incident?.service_name}</div>
                  <div className="flex">
                    {" "}
                    <div className="incident-count">{incident?.curr_count}</div>
                    <div className="incident-trend ml-3">
                      {incident?.curr_count > incident?.prev_count ? (
                        <span className="trend up">
                          <UpArrow />
                        </span>
                      ) : (
                        <span className="trend down">
                          <DownArrow />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div style={noDataStyle}>
              <span>
                <FontAwesomeIcon icon={faInfoCircle} /> No data available
              </span>
            </div>
          )}
        </div>
        <div className="resolved">
          <h2 className="resolved-title">Tickets resolved</h2>
          <div className="resolved-count flex p-5">
            <h2 className="count">
              {data?.ticketsResolved[0]?.resolved_tickets}
            </h2>
            <p className="month">This month</p>
          </div>
          <div>
            {/* {pidata?.length > 0 ? ( */}
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Legend verticalAlign="top" height={36} />

                <Pie
                  data={pidata}
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="90%"
                  fill="#00905E"
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {pidata.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            {/* ) : ( */}
            {/* <div style={noDataStyle}>
                <span>
                  <FontAwesomeIcon icon={faInfoCircle} /> No data available
                </span>
              </div>
            )} */}
          </div>
        </div>
        <div className="incidents">
          <h2 className="incidents-title">Tickets by department (per month)</h2>
          {data?.ticketsByDept?.length > 0 ? (
            <>
              {data?.ticketsByDept?.map((incident, index) => (
                <div key={index} className="incident-row">
                  <div className="flex">
                    <div className="incident-name">
                      {incident?.service_dept_name}
                    </div>
                  </div>
                  <div className="flex">
                    {" "}
                    <div className="incident-count">{incident?.curr_count}</div>
                    <div className="incident-trend ml-3">
                      {incident?.curr_count > incident?.prev_count ? (
                        <span className="trend up">
                          <UpArrow />
                        </span>
                      ) : (
                        <span className="trend down">
                          <DownArrow />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div style={noDataStyle}>
              <span>
                <FontAwesomeIcon icon={faInfoCircle} /> No data available
              </span>
            </div>
          )}
        </div>
        <div className="incidents">
          <h2 className="incidents-title">Submission category (per month)</h2>
          {data?.ticketsBySubCat?.length > 0 ? (
            <>
              {data?.ticketsBySubCat?.map((incident, index) => (
                <div key={index} className="incident-row">
                  <div className="flex">
                    <div className="incident-name">
                      {incident?.service_type_name}
                    </div>
                  </div>
                  <div className="flex">
                    {" "}
                    <div className="incident-count">{incident?.curr_count}</div>
                    <div className="incident-trend ml-3">
                      {incident?.curr_count > incident?.prev_count ? (
                        <span className="trend up">
                          <UpArrow />
                        </span>
                      ) : (
                        <span className="trend down">
                          <DownArrow />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div style={noDataStyle}>
              <span>
                <FontAwesomeIcon icon={faInfoCircle} /> No data available
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WardsReport;
